/**
 * "Paid - awaiting serial" work queue (sales dashboard card).
 *
 * Lists orders stuck in the Proposed state whose Shopify draft shows PAID but
 * which have no serial number - the Bill Nizolek gap (paid 2026-10-30, nobody
 * noticed until Pete said so). Pete assigns serials in the factory system
 * (Ullr); Skafer is READ-ONLY for serials (FACTORY_OWNED_ORDERFORM_FIELDS,
 * fail closed), so this card is a queue, never an editor.
 *
 * ZOMBIE FILTER (2026-11-02, Kenny/Todd/Marcie): a stale proposal row whose
 * customer later ordered the same design (same length token) on a LATER order
 * that carries a serial is superseded - the ski already built and shipped.
 * Those rows are excluded from the queue and reported as auto-cleared, so the
 * queue only ever shows orders that GENUINELY await Pete's serial assignment.
 *
 * Data path (all read-only, existing endpoints):
 *   GET /api/v3/fjell/order?limit=1000&offset=0          -> order rows
 *     filter: orderStateId === PROPOSED_STATE_ID, no serialNum, not deleted
 *   for each candidate:
 *     GET /api/v3/fjell/order/:id                        -> detail (buyer + skier)
 *     GET /api/v3/fjell/customer/:buyerId                -> shopifyCustomerPk
 *     GET /api/v3/fjell/skafer/order/:id/synced?shopifyCustomerPk=:ck
 *       include when synced.financialStatus === 'PAID'
 *     GET /api/v3/fjell/order/search?query=<skier name>  -> supersession check
 *
 * On-demand sweep (button press), NOT on dashboard load - a full sweep is
 * several calls per candidate; polling that on every render would hammer the
 * API. The daily payment-detector cron is the push side.
 */
import { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, LinearProgress, Stack, Typography,
} from '@mui/material';
import type { PaymentStatus } from '../contracts/proposalSync';

export type ApiFn = (path: string, token: string, init?: RequestInit) => Promise<unknown>;

const PROPOSED_STATE_ID = 4;

export interface PaidAwaitingRow {
  orderId: number;
  name: string;
  serialNum: string | null;
  totalPrice: string;
  paymentStatus: PaymentStatus;
  lastSyncedAt: string;
}

/** A zombie proposal auto-cleared from the queue: the design shipped on a later order. */
export interface ClearedRow {
  orderId: number;
  name: string;
  supersededByOrderId: number;
  supersededByName: string;
  supersedingSerial: string | number | null;
  supersedingState: string;
}

export interface SweepResult {
  queue: PaidAwaitingRow[];
  cleared: ClearedRow[];
}

function itemsOf(body: unknown): Record<string, unknown>[] {
  const b = body as { items?: Record<string, unknown>[] } | Record<string, unknown>[];
  return Array.isArray(b) ? b : b?.items ?? [];
}

/**
 * The design-length token of an order name ('Kenny Park 170-101 260153' -> '170',
 * 'Todd Ablowitz 190' -> '190'). Supersession matches on length: waist can be
 * retuned between the proposal and the final order, the length cannot.
 */
export function lengthToken(name: string): string {
  for (const tok of String(name || '').split(' ')) {
    const digits = tok.split('-')[0].replace(/[^0-9]/g, '');
    if (digits.length === 3 && tok.indexOf('-') === 3) return digits;
    if (tok.length === 3 && /^\d{3}$/.test(tok)) return tok;
  }
  return '';
}

/**
 * A candidate is superseded when the same customer has a LATER order with a
 * serial whose length token matches the candidate's design length.
 */
export function findSuperseding(
  candidate: { id: number; name: string },
  customerOrders: Record<string, unknown>[],
): { orderId: number; name: string; serialNum: string | number | null; state: string } | null {
  const len = lengthToken(candidate.name);
  if (!len) return null;
  for (const o of customerOrders) {
    const oid = Number(o.id);
    if (!(oid > candidate.id)) continue;
    if (!o.serialNum) continue;
    if (lengthToken(String(o.name ?? '')) !== len) continue;
    return {
      orderId: oid,
      name: String(o.name ?? `Order #${oid}`),
      serialNum: (o.serialNum as string | number) ?? null,
      state: String((o.orderState as { name?: string } | undefined)?.name ?? ''),
    };
  }
  return null;
}

/** Buyer -> shopifyCustomerPk, exactly like ProposalPayments.resolveShopifyCustomer. */
async function resolveShopifyCustomer(api: ApiFn, token: string, orderId: number): Promise<string | null> {
  const order = await api(`/api/v3/fjell/order/${orderId}`, token) as Record<string, unknown>;
  const row = (order as { item?: Record<string, unknown> }).item ?? order;
  const buyerId = Number(row.buyerId);
  if (!buyerId) return null;
  const customer = await api(`/api/v3/fjell/customer/${buyerId}`, token) as Record<string, unknown>;
  const crow = (customer as { item?: Record<string, unknown> }).item ?? customer;
  const pk = crow.shopifyCustomerId;
  return pk ? String(pk) : null;
}

/** One sweep across the whole order list. Sequential candidates: honest, no rate surprises. */
export async function sweepPaidAwaiting(api: ApiFn, token: string): Promise<SweepResult> {
  const queue: PaidAwaitingRow[] = [];
  const cleared: ClearedRow[] = [];
  let offset = 0;
  for (;;) {
    const page = await api(`/api/v3/fjell/order?limit=1000&offset=${offset}`, token);
    const orders = itemsOf(page);
    const candidates = orders.filter((o) =>
      Number(o.orderStateId) === PROPOSED_STATE_ID && !o.serialNum && !o.isDeleted);
    for (const order of candidates) {
      const orderId = Number(order.id);
      try {
        const ck = await resolveShopifyCustomer(api, token, orderId);
        if (!ck) continue;
        const res = await api(`/api/v3/fjell/skafer/order/${orderId}/synced?shopifyCustomerPk=${ck}`, token);
        if (res === null || res === undefined) continue;
        const synced = res as { financialStatus?: string; totalPrice?: string; lastSyncedAt?: string };
        if (synced.financialStatus !== 'PAID') continue;
        // zombie check: did this customer's design ship on a later serial-bearing order?
        let superseded: ReturnType<typeof findSuperseding> = null;
        try {
          const detail = await api(`/api/v3/fjell/order/${orderId}`, token) as Record<string, unknown>;
          const drow = (detail as { item?: Record<string, unknown> }).item ?? detail;
          const skier = drow.skier as Record<string, unknown> | undefined;
          const skierName = String(skier?.name ?? '');
          if (skierName) {
            const found = await api(`/api/v3/fjell/order/search?query=${encodeURIComponent(skierName)}`, token);
            superseded = findSuperseding({ id: orderId, name: String(order.name ?? '') }, itemsOf(found)
              .filter((o) => Number(o.skierId) === Number(drow.skierId) || Number(o.buyerId) === Number(drow.skierId)));
          }
        } catch { /* supersession check is best-effort; the row stays queued */ }
        if (superseded) {
          cleared.push({
            orderId, name: String(order.name ?? `Order #${orderId}`),
            supersededByOrderId: superseded.orderId, supersededByName: superseded.name,
            supersedingSerial: superseded.serialNum, supersedingState: superseded.state,
          });
          continue;
        }
        queue.push({
          orderId,
          name: String(order.name ?? `Order #${orderId}`),
          serialNum: null,
          totalPrice: String(synced.totalPrice ?? '-'),
          paymentStatus: 'PAID',
          lastSyncedAt: String(synced.lastSyncedAt ?? ''),
        });
      } catch {
        // A single unreadable candidate never blocks the sweep; it just stays invisible
        // here. The cron detector covers the same gap server-side.
      }
    }
    if (orders.length < 1000) break;
    offset += 1000;
  }
  return { queue, cleared };
}

export function PaidAwaitingSerial({ api, token, onOpenOrder }: {
  api: ApiFn;
  token: string;
  /** Dashboard handler: open the order's detail view for a closer look. */
  onOpenOrder?: (orderId: number) => void;
}) {
  const [result, setResult] = useState<SweepResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setError(null);
    try {
      setResult(await sweepPaidAwaiting(api, token));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const rows = result?.queue ?? null;
  const cleared = result?.cleared ?? [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Paid - awaiting serial"
        subheader="Proposed orders whose invoice is paid but which have no serial number yet."
        action={<Button onClick={run} disabled={busy} variant={rows === null ? 'contained' : 'outlined'}>
          {rows === null ? 'Check now' : 'Refresh'}</Button>} />
      <CardContent>
        {busy && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {rows !== null && rows.length === 0 && !busy &&
          <Typography color="text.secondary">No paid-but-proposed orders - all clear.</Typography>}
        {rows !== null && rows.length > 0 && (
          <Stack spacing={1}>
            {rows.map((row) => (
              <Box key={row.orderId} onClick={() => onOpenOrder?.(row.orderId)}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, cursor: onOpenOrder ? 'pointer' : 'default' }}>
                <Typography fontWeight={650}>{row.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Order #{row.orderId} · paid {row.totalPrice} · no serial yet · last synced {row.lastSyncedAt}
                </Typography>
              </Box>
            ))}
            <Alert severity="info" sx={{ mt: 1 }}>
              Serial numbers are assigned in the factory system (Ullr) - Skafer is read-only for serials.
              Assign there and the order moves to In-progress; it then drops off this list.
            </Alert>
          </Stack>
        )}
        {cleared.length > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {cleared.length} older proposal{cleared.length === 1 ? '' : 's'} auto-cleared - the design shipped
            on a later order:{' '}
            {cleared.map((c) => `#${c.orderId} ${c.name.trim()} -> #${c.supersededByOrderId} (serial ${c.supersedingSerial}, ${c.supersedingState})`).join('; ')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
